import { watch, type Ref } from "vue";
import { DS_LIGHT_BOX_CLASSES } from "../constants/classes";
import {
  DS_LIGHT_BOX_KEYBOARD_EXCLUSION_SELECTOR,
  DS_LIGHT_BOX_POINTER_EXCLUSION_SELECTOR,
} from "../constants/selectors";

const matchesControl = (target: EventTarget | null, selector: string) =>
  target instanceof Element && Boolean(target.closest(selector));
const isInteractive = (target: EventTarget | null) => matchesControl(target, DS_LIGHT_BOX_POINTER_EXCLUSION_SELECTOR);

export const useLightBoxNavigation = ({
  itemKey,
  next,
  previous,
}: {
  itemKey: Ref<string>;
  next: () => void;
  previous: () => void;
}) => {
  let dragStart: { x: number; y: number; id: number } | undefined;
  const cancelDrag = () => {
    dragStart = undefined;
  };
  watch(itemKey, cancelDrag);

  const navigateKey = (event: KeyboardEvent) => {
    const action = ({ ArrowLeft: previous, ArrowRight: next } as Record<string, (() => void) | undefined>)[event.key];
    if (!action) return;
    event.preventDefault();
    action();
  };
  const onKeydown = (event: KeyboardEvent) => {
    if (event.defaultPrevented) return;
    if (matchesControl(event.target, `.${DS_LIGHT_BOX_CLASSES.Actions}`)) return;
    if (matchesControl(event.target, DS_LIGHT_BOX_KEYBOARD_EXCLUSION_SELECTOR)) return;
    navigateKey(event);
  };
  const onPointerDown = (event: PointerEvent) => {
    cancelDrag();
    if (event.button !== 0 || isInteractive(event.target)) return;
    dragStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  };
  const navigateDrag = (dx: number, dy: number) => {
    if (Math.abs(dx) < 50 || Math.abs(dx) <= Math.abs(dy)) return;
    if (dx < 0) next();
    else previous();
  };
  const onPointerUp = (event: PointerEvent) => {
    const start = dragStart;
    cancelDrag();
    if (!start || start.id !== event.pointerId || isInteractive(event.target)) return;
    navigateDrag(event.clientX - start.x, event.clientY - start.y);
  };
  const onPointerCancel = () => cancelDrag();
  return { onKeydown, onPointerDown, onPointerUp, onPointerCancel };
};
